import React, { useState, useEffect } from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { userSession } from '../../../services/localStorageService';

const PrivacyControls = ({ userRole, onSave }) => {
  const currentUser = userSession?.get();
  const [settings, setSettings] = useState({
    anonymityLevel: 'strict',
    allowDataAnalytics: true,
    shareWithPartners: false,
    allowProfileIndexing: false,
    hideFromSearch: false,
    requireRevealApproval: true,
    autoDeleteMessages: false,
    messageRetention: '90days',
    blockScreenshots: true,
    twoFactorAuth: false
  });

  useEffect(() => {
    const savedPrefs = localStorage.getItem(`preferences_privacy_${currentUser?.id}`);
    if (savedPrefs) {
      setSettings(JSON.parse(savedPrefs));
    }
  }, [currentUser?.id]);

  const handleToggle = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev?.[key] }));
  };

  const handleSelectChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    localStorage.setItem(`preferences_privacy_${currentUser?.id}`, JSON.stringify(settings));
    onSave?.('Privacy settings saved successfully');
  };

  const anonymityOptions = [
    { 
      value: 'strict', 
      label: 'Strict', 
      description: 'Maximum anonymity - no identifying information shared' 
    },
    { 
      value: 'balanced', 
      label: 'Balanced', 
      description: 'Some general information visible after mutual interest' 
    },
    { 
      value: 'relaxed', 
      label: 'Relaxed', 
      description: 'More profile details visible to potential matches' 
    }
  ];

  const retentionOptions = [
    { value: '30days', label: '30 Days' },
    { value: '90days', label: '90 Days' },
    { value: '180days', label: '6 Months' },
    { value: '1year', label: '1 Year' },
    { value: 'never', label: 'Never Delete' }
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-bold text-[#111827] mb-2">Privacy Controls</h2>
        <p className="text-sm text-[#6B7280]">
          Manage your anonymity settings and control how your data is used
        </p>
      </div>

      {/* Anonymity Level */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-[#E5E7EB]">
          <Icon name="Shield" size={20} color="#5B2DFF" />
          <h3 className="text-lg font-semibold text-[#111827]">Anonymity Level</h3>
        </div>
        
        <Select
          label="Choose your anonymity level"
          description="This controls how much information is visible to others"
          options={anonymityOptions}
          value={settings?.anonymityLevel}
          onChange={(value) => handleSelectChange('anonymityLevel', value)}
        />

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3">
          <Icon name="Info" size={20} color="#3b82f6" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-1">Current Level: {settings?.anonymityLevel?.charAt(0)?.toUpperCase() + settings?.anonymityLevel?.slice(1)}</p>
            <p className="text-blue-700">
              {settings?.anonymityLevel === 'strict' && 'Your identity is fully protected. Only anonymous profile details are visible.'}
              {settings?.anonymityLevel === 'balanced' && 'General information becomes visible after mutual interest is established.'}
              {settings?.anonymityLevel === 'relaxed' && 'More profile details are visible to help potential matches find you.'}
            </p>
          </div>
        </div>
      </div>

      {/* Identity Protection */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-[#E5E7EB]">
          <Icon name="Lock" size={20} color="#5B2DFF" />
          <h3 className="text-lg font-semibold text-[#111827]">Identity Protection</h3>
        </div>
        
        <Checkbox
          checked={settings?.requireRevealApproval}
          onChange={() => handleToggle('requireRevealApproval')}
          label="Require approval for identity reveals"
          description="You must approve all requests to reveal your identity"
        />

        <Checkbox
          checked={settings?.hideFromSearch}
          onChange={() => handleToggle('hideFromSearch')}
          label="Hide from public search"
          description="Your profile won't appear in search results outside BlinD"
        />

        <Checkbox
          checked={settings?.blockScreenshots}
          onChange={() => handleToggle('blockScreenshots')}
          label="Block screenshots (where supported)"
          description="Attempt to prevent screenshots of your profile and messages"
        />
      </div>

      {/* Data Sharing */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-[#E5E7EB]">
          <Icon name="Database" size={20} color="#5B2DFF" />
          <h3 className="text-lg font-semibold text-[#111827]">Data Sharing</h3>
        </div>
        
        <Checkbox
          checked={settings?.allowDataAnalytics}
          onChange={() => handleToggle('allowDataAnalytics')}
          label="Allow anonymous analytics"
          description="Help improve BlinD by sharing anonymous usage data"
        />

        <Checkbox
          checked={settings?.shareWithPartners}
          onChange={() => handleToggle('shareWithPartners')}
          label="Share with trusted partners"
          description="Allow sharing anonymized data with verified recruitment partners"
        />

        <Checkbox
          checked={settings?.allowProfileIndexing}
          onChange={() => handleToggle('allowProfileIndexing')}
          label="Allow profile indexing"
          description="Let search engines index your anonymous profile for better discoverability"
        />
      </div>

      {/* Message Privacy */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-[#E5E7EB]">
          <Icon name="MessageSquare" size={20} color="#5B2DFF" />
          <h3 className="text-lg font-semibold text-[#111827]">Message Privacy</h3>
        </div>
        
        <Checkbox
          checked={settings?.autoDeleteMessages}
          onChange={() => handleToggle('autoDeleteMessages')}
          label="Auto-delete old messages"
          description="Automatically delete messages after a specified period"
        />

        {settings?.autoDeleteMessages && (
          <div className="ml-6">
            <Select
              label="Message retention period"
              options={retentionOptions}
              value={settings?.messageRetention}
              onChange={(value) => handleSelectChange('messageRetention', value)}
            />
          </div>
        )}
      </div>

      {/* Security */}
      {userRole === 'employer' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 pb-3 border-b border-[#E5E7EB]">
            <Icon name="ShieldCheck" size={20} color="#5B2DFF" />
            <h3 className="text-lg font-semibold text-[#111827]">Account Security</h3>
          </div>
          
          <Checkbox
            checked={settings?.twoFactorAuth}
            onChange={() => handleToggle('twoFactorAuth')}
            label="Enable two-factor authentication"
            description="Add an extra layer of security to your account"
          />
        </div>
      )}

      {/* Save Button */}
      <div className="pt-6 border-t border-[#E5E7EB] flex justify-end">
        <Button
          onClick={handleSave}
          iconName="Save"
          className="bg-gradient-to-r from-[#5B2DFF] to-[#7C3FFF]"
        >
          Save Privacy Settings
        </Button>
      </div>
    </div>
  );
};

export default PrivacyControls;